/**
 * Employment change types: the managed list behind the employment history timeline.
 *
 * Route: /my-workspace/employment-change-types
 *
 * TWO KINDS OF ROW, and the screen has to make the difference obvious:
 *
 *   Built in   the 13 the system writes itself when it notices a field changed.
 *              Renameable, because every query matches on the id and never on the
 *              text. Cannot be switched off or deleted, because the code writes
 *              them regardless and the timeline would then point at nothing.
 *
 *   Custom     added here by HR. Nothing writes them automatically, they are for
 *              entries added to a person's timeline by hand. Fully editable, and
 *              deletable only while unused.
 *
 * See the node portal repo, docs/user-role-tag-with-history/README.md
 */
import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useSnackbar } from 'notistack';

import GlobalWrapper from '../../../../components/Wrapper/GlobalWrapper';
import apiRequest from '../../../../api/request';
import {
  helpForChangeType,
  HELP_TOOLTIP_PROPS,
} from '../../../../utils/constants/employmentChangeTypes';

/**
 * Editing is OFF for now, so this screen is read only.
 *
 * Adding a custom type currently achieves nothing: the 13 built in types are written
 * because the code watches a specific column, and a custom type has no column behind
 * it. Until there is a way to add a line to somebody's timeline by hand, a custom
 * type can never appear anywhere.
 *
 * Set this to true once that exists. The add, rename, delete and switch handlers
 * below are all written and working, they are only hidden.
 */
const EDITING_ENABLED = false;

/** "Salary Revision" -> SALARY_REVISION, shown so HR can see what will be stored. */
const previewCode = (name) =>
  String(name || '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

export default function EmploymentChangeTypes() {
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [types, setTypes] = useState([]);
  const [busy, setBusy] = useState(false);

  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const [editing, setEditing] = useState(null); // the row being renamed
  const [editName, setEditName] = useState('');

  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    const res = await apiRequest({ method: 'get', url: 'employment-change-types' });
    setTypes(res?.status === 'success' ? res.data.changeTypes : []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const done = (res, okMessage) => {
    if (res?.status === 'success') {
      enqueueSnackbar(okMessage, { variant: 'success' });
      load();
      return true;
    }
    enqueueSnackbar(res?.message || 'Could not save', { variant: 'error' });
    return false;
  };

  const addType = async () => {
    setBusy(true);
    const res = await apiRequest({
      method: 'post',
      url: 'employment-change-types',
      data: { name: newName },
    });
    setBusy(false);
    if (done(res, 'Change type added')) {
      setAddOpen(false);
      setNewName('');
    }
  };

  const renameType = async () => {
    setBusy(true);
    const res = await apiRequest({
      method: 'patch',
      url: `employment-change-types/${editing.id}`,
      data: { name: editName },
    });
    setBusy(false);
    if (done(res, 'Renamed')) setEditing(null);
  };

  const toggleActive = async (row) => {
    const res = await apiRequest({
      method: 'patch',
      url: `employment-change-types/${row.id}`,
      data: { isActive: row.isActive ? 0 : 1 },
    });
    done(res, row.isActive ? 'Switched off' : 'Switched on');
  };

  const removeType = async () => {
    setBusy(true);
    const res = await apiRequest({
      method: 'delete',
      url: `employment-change-types/${confirmDelete.id}`,
    });
    setBusy(false);
    if (done(res, 'Deleted')) setConfirmDelete(null);
  };

  const addButton = EDITING_ENABLED ? (
    <Button
      size="small"
      variant="contained"
      startIcon={<Icon icon="mdi:plus" />}
      onClick={() => setAddOpen(true)}
    >
      Add change type
    </Button>
  ) : null;

  if (loading) return <GlobalWrapper title="Change Types" skelton />;

  return (
    <GlobalWrapper title="Change Types" actions={addButton} skelton={false}>
      <Stack spacing={2}>
        <Paper variant="outlined">
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Applies to</TableCell>
                  <TableCell>Kind</TableCell>
                  <TableCell align="center">Used</TableCell>
                  <TableCell align="center">Active</TableCell>
                  {EDITING_ENABLED && <TableCell align="right">&nbsp;</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {types.map((row) => (
                  <TableRow key={row.id} sx={row.isActive ? undefined : { opacity: 0.5 }}>
                    <TableCell>
                      {/* the explanation lives on the name, because that is what
                          HR reads first and what they see on the timeline too */}
                      <Tooltip
                        title={helpForChangeType(row.id, row.isSystem)}
                        placement="right"
                        arrow
                        componentsProps={HELP_TOOLTIP_PROPS}
                      >
                        <Stack direction="row" spacing={0.5} alignItems="center"
                          sx={{ cursor: 'help', width: 'fit-content' }}>
                          <Typography variant="body2">{row.name}</Typography>
                          <Icon icon="mdi:help-circle-outline" style={{ opacity: 0.45 }} />
                        </Stack>
                      </Tooltip>
                      <Typography variant="caption" color="text.secondary">
                        {row.code}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Tooltip
                        title={helpForChangeType(row.id, row.isSystem)}
                        placement="top"
                        arrow
                        componentsProps={HELP_TOOLTIP_PROPS}
                      >
                        <Typography variant="body2" color="text.secondary"
                          sx={{ cursor: 'help' }}>
                          {row.fieldName
                            ? `when ${row.fieldName.replace(/_id$/, '').replace(/_/g, ' ')} changes`
                            : row.isSystem
                              ? 'on joining, exit or rejoin'
                              : 'added by hand'}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        label={row.isSystem ? 'Built in' : 'Custom'}
                        color={row.isSystem ? 'default' : 'primary'}
                      />
                    </TableCell>

                    <TableCell align="center">
                      <Typography variant="body2">{row.timesUsed}</Typography>
                    </TableCell>

                    <TableCell align="center">
                      {/* a built in type cannot be switched off, the system writes
                          it regardless and the timeline would reference a type
                          nothing displays */}
                      <Tooltip
                        title={
                          !EDITING_ENABLED
                            ? 'Read only for now'
                            : row.isSystem
                              ? 'Built in types are always active'
                              : row.isActive
                                ? 'Switch off'
                                : 'Switch on'
                        }
                      >
                        <span>
                          <Switch
                            size="small"
                            checked={Boolean(row.isActive)}
                            disabled={!EDITING_ENABLED || Boolean(row.isSystem)}
                            onChange={() => toggleActive(row)}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>

                    {EDITING_ENABLED && (
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Rename">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditing(row);
                              setEditName(row.name);
                            }}
                          >
                            <Icon icon="mdi:pencil-outline" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip
                          title={
                            row.isSystem
                              ? 'Built in types cannot be deleted'
                              : row.timesUsed > 0
                                ? `Used ${row.timesUsed} time(s), switch it off instead`
                                : 'Delete'
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              disabled={Boolean(row.isSystem) || row.timesUsed > 0}
                              onClick={() => setConfirmDelete(row)}
                            >
                              <Icon icon="mdi:delete-outline" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Stack>

      {/* ---------------- add ---------------- */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add change type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1 }}
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">
              Stored as &nbsp;<strong>{previewCode(newName) || '…'}</strong>
            </Typography>
          </Box>
          <Alert severity="info" sx={{ mt: 2 }}>
            Custom types are never written automatically. They can only be added to
            somebody&apos;s timeline by hand.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addType} disabled={busy || !newName.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- rename ---------------- */}
      <Dialog open={Boolean(editing)} onClose={() => setEditing(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rename change type</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Name"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            sx={{ mt: 1 }}
          />
          {editing?.isSystem && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Only the name changes. The code <strong>{editing.code}</strong> and the
              field it watches stay as they are, so nothing that already uses this
              type breaks.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={renameType} disabled={busy || !editName.trim()}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---------------- delete ---------------- */}
      <Dialog open={Boolean(confirmDelete)} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete change type</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Delete <strong>{confirmDelete?.name}</strong>? It has never been used, so
            nothing on any timeline refers to it.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={removeType} disabled={busy}>
            {busy ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </GlobalWrapper>
  );
}
